import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';

export enum SuSBewerberStatus {
  BEWERBUNG = 'Bewerbung',
  ABGELEHNT = 'abgelehnt',
  GESPRAECHSTERMIN = 'Gesprächstermin',
  ZUSAGE = 'Zusage',
  ABSAGE = 'Absage',
  WARTELISTE = 'Warteliste',
  PRIO_WARTELISTE = 'Prio Warteliste',
  HOSPITATION = 'Hospitation',
}

export function getSuSBewerberStatusDescription(status: Maybe<SuSBewerberStatus>) {
  const descrption = {
    [SuSBewerberStatus.BEWERBUNG]: 'Initaler Status und automatisch vergeben.',
    [SuSBewerberStatus.ABGELEHNT]:
      'SuS wurde von den Bewerbungsbearbeitenden abgelehnt. Wird ein Jahr nach Anlage automatisch gelöscht.',
    [SuSBewerberStatus.GESPRAECHSTERMIN]: 'Es wurde ein Gesprächstermin gebucht.',
    [SuSBewerberStatus.ZUSAGE]: 'SuS ist angenommen und wird aktiv.',
    [SuSBewerberStatus.ABSAGE]:
      'Sorgeberechtigte haben die Bewerbung zurückgezogen. Wird ein Jahr nach Anlage automatisch gelöscht.',
    [SuSBewerberStatus.WARTELISTE]: 'Wartet auf Zusage, bis ein Platz zur Verfügung steht.',
    [SuSBewerberStatus.PRIO_WARTELISTE]:
      'Wartet auf Zusage, bis ein Platz zur Verfügung steht, wobei eine realistischere Chance besteht.',
    [SuSBewerberStatus.HOSPITATION]: 'Es ist ein näheres Kennenlernen notwendig, bevor eine Zusage erfolgen kann.',
  };
  return (
    descrption[status ?? ''] ??
    'Kein Status vorhanden. SuS wurde manuell angelegt oder ist entstanden bevor es diese Funktion gab.'
  );
}

/**
 * An application that ended - rejected by the school (Ablehnungsgrund or status) or withdrawn by the guardians.
 * It turns the applicant and its guardians into old applicants, which the nightly cleanup removes once the
 * person is a year old.
 */
export function isRejectedSuSBewerbung(
  schuelerDaten: Maybe<Pick<SchuelerDaten, 'ablehnungGrund' | 'susBewerberStatus'>>,
): boolean {
  return (
    !!schuelerDaten?.ablehnungGrund ||
    schuelerDaten?.susBewerberStatus === SuSBewerberStatus.ABGELEHNT ||
    schuelerDaten?.susBewerberStatus === SuSBewerberStatus.ABSAGE
  );
}

/**
 * The year the child first entered school, derived instead of stored. The form asks how many school years
 * the child will have attended by the end of primary school, and children apply during their last primary
 * school year - so that year is the last of them.
 */
export function getEinschulungsJahr(
  schuelerDaten: Maybe<Pick<SchuelerDaten, 'bewerbungsDate' | 'schoolyearsAfterElementary'>>,
): Maybe<number> {
  const bewerbungsDate = schuelerDaten?.bewerbungsDate;
  const schoolyears = schuelerDaten?.schoolyearsAfterElementary;
  if (!bewerbungsDate || !schoolyears) return null;

  // the school year starts in August, so an application from January to July belongs to last year's school year
  const schoolyearStart = bewerbungsDate.getUTCFullYear() - (bewerbungsDate.getUTCMonth() < 7 ? 1 : 0);
  return schoolyearStart - (schoolyears - 1);
}

@Entity()
export class SchuelerDaten extends AdornisEntity {
  static override _class = 'Schueler';

  @Field(() => String)
  schuelerNr: Maybe<string>;

  @Field(() => String)
  heinNummer: Maybe<string>;

  @Field(() => [String], { default: v => v ?? [] })
  eingeladeneSorgeberechtigte: Maybe<string[]>;

  @Field(() => [String], { default: v => v ?? [] })
  eingeladeneSpender: Maybe<string[]>;

  @Field(() => String)
  potentialMartinusElementarySchoolID: Maybe<string>;

  @Field(() => String)
  otherSchool: Maybe<string>;

  @Field(() => Boolean)
  hasSiblingsAtMartinusSchools: Maybe<boolean>;

  @Field(() => Int)
  lastGradeGerman: Maybe<number>;

  @Field(() => String)
  wantedKlassenstufe: Maybe<string>;

  @Field(() => String)
  currentKlassenstufeAtBewerbungsDate: Maybe<string>;

  @Field(() => Date)
  bewerbungsDate: Maybe<Date>;

  @Field(() => String)
  motivation: Maybe<string>;

  @Field(() => String)
  schulwechselMotivation: Maybe<string>;

  @Field(() => String)
  ablehnungGrund: Maybe<string>;

  @Field(() => Boolean)
  languageAtHomeIsGerman: Maybe<boolean>;

  @Field(() => Int)
  lastGradeMath: Maybe<number>;

  @Field(() => Int)
  lastGradeSachunterricht: Maybe<number>;

  @Field(() => String)
  interestInNativeLanguageCourses: Maybe<string>;

  @Field(() => Boolean)
  interestInWholeDaySchooling: Maybe<boolean>;

  @Field(() => Int)
  schoolyearsAfterElementary: Maybe<number>;

  @Field(() => ID)
  learningDisabilitiesFileID: Maybe<string>;

  @DataLoadedField(() => AdornisFile, ({ learningDisabilitiesFileID }) => learningDisabilitiesFileID)
  learningDisabilitiesFile: Maybe<AdornisFile>;

  @Field(() => ID)
  sorgerechtsurteilFileID: Maybe<string>;

  @DataLoadedField(() => AdornisFile, ({ sorgerechtsurteilFileID }) => sorgerechtsurteilFileID)
  sorgerechtsurteilFile: Maybe<AdornisFile>;

  @Field(() => [ID], { default: v => v ?? [] })
  primarySchoolTranscripts!: string[];

  @DataLoadedField(() => [AdornisFile], ({ primarySchoolTranscripts }) => primarySchoolTranscripts)
  primarySchoolTranscriptsFiles: Maybe<AdornisFile[]>;

  @Field(() => Boolean)
  participationReligion: Maybe<boolean>;

  // when did a person arrive in germany. Edosys needs this to calculate the Schulpflicht
  @Field(() => Date)
  zuzugsDate: Maybe<Date>;

  @Field(() => String)
  internalNotes: Maybe<string>;

  /**
   * If the person is a possible sus bewerber, it can have a special status. Not every bewerber gets accpeted directly to a student.
   * Some are put on hold, some need more interviews.
   */
  @Field(() => String)
  susBewerberStatus: Maybe<string>;

  /**
   * The last day an update link sent with a deadline works, a calendar day. Any submission of the form clears it,
   * an unanswered running application is declined by the nightly job once it has passed.
   */
  @Field(() => Date)
  updateDeadline: Maybe<Date>;
}
